import { useEffect, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { api, assetUrl, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const input = "w-full rounded-lg border border-white/10 bg-[#07090E] px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60";
const INPUT_TYPE = { number: "number", date: "date", datetime: "datetime-local", email: "email", url: "url" };

const toInput = (f, v) => (v == null ? "" : f.type === "datetime" ? String(v).slice(0, 16) : v);

const FieldInput = ({ appId, field, value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const id = `record-field-${field.name}`;
  if (field.type === "long_text") return <textarea id={id} rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} className={input} />;
  if (field.type === "boolean") return <input id={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={id} className="h-4 w-4 accent-cyan-400" />;
  if (field.type === "select") {
    return (
      <select id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} className={input}>
        <option value="" className="bg-[#0B0F18]">—</option>
        {field.options.map((o) => <option key={o} value={o} className="bg-[#0B0F18]">{o}</option>)}
      </select>
    );
  }
  if (field.type === "image") {
    const upload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      const fd = new FormData(); fd.append("file", file);
      try { const { data } = await api.post(`/manage/apps/${appId}/files`, fd); onChange(data.url); }
      finally { setUploading(false); }
    };
    return (
      <div className="flex items-center gap-3">
        {value && <img src={assetUrl(value)} alt="" className="h-16 w-24 rounded-lg border border-white/10 object-cover" />}
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-200 hover:border-cyan-400/50">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {value ? "Replace" : "Upload"}
          <input type="file" accept="image/*" onChange={upload} data-testid={id} className="hidden" />
        </label>
      </div>
    );
  }
  return <input id={id} type={INPUT_TYPE[field.type] || "text"} value={toInput(field, value)} onChange={(e) => onChange(e.target.value)} data-testid={id} className={input} />;
};

export const RecordDialog = ({ appId, collection, record, open, onClose, onSaved }) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValues(record?.data || {}); setErrors({}); }, [record, open]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErrors({});
    try {
      if (record) await api.patch(`/manage/apps/${appId}/records/${record.id}`, { data: values });
      else await api.post(`/manage/apps/${appId}/collections/${collection.name}/records`, values);
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErrors(d?.fields || { _: formatApiErrorDetail(d?.message || d) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="record-dialog" className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0B0F18] text-white sm:max-w-lg">
        <DialogHeader><DialogTitle>{record ? "Edit entry" : `New ${collection.label} entry`}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          {collection.fields.map((f) => (
            <label key={f.name} className="block text-xs text-slate-400">
              <span className="mb-1 block">{f.label}{f.required && <span className="text-cyan-300"> *</span>}</span>
              <FieldInput appId={appId} field={f} value={values[f.name]} onChange={(v) => setValues({ ...values, [f.name]: v })} />
              {errors[f.name] && <span data-testid={`record-error-${f.name}`} className="mt-1 block text-red-400">{errors[f.name]}</span>}
            </label>
          ))}
          {errors._ && <p className="text-sm text-red-400">{errors._}</p>}
          <button type="submit" disabled={busy} data-testid="record-save-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
